import React, { useCallback, useEffect, useMemo, useRef } from "react";
import classnames from "classnames";
import { Preloader } from "@smartsuite/react-ui/lib";
import { Doc, EditorSchema, Editor, generateSchema, isDocEmpty } from "@smartsuite/smartdoc";
import { EditorView } from "@smartsuite/smartdoc/lib/esm/core/types";
import { yDocToProsemirrorJSON } from "y-prosemirror";
import { fromByteArray } from "base64-js";
import { init } from "filestack-js";
import * as YJS from "yjs";

import { useYjsProvider } from "../extensions/collab/useYjsProvider";
import { commentsPreset, documentViewPreset, fullPreset, simplePreset } from "../extensions";
import { postMessage } from "../helpers/events";
import { SSAiExtensionService } from "../extensions/ai/ai-extension.service";
import { ReportLabelInput } from "../ReportLabelInput";
import { useAppConfig, useAppConfigActions } from "../context/AppConfigContext";
import { useIOSViewport } from "../hooks/useIOSViewport";
import { getEnvBaseUrl } from "../config/env-url-map";
import { isSanitizeDocReportEmpty } from "../helpers/sanitizeDoc";
import {
  describeIncomingDocMismatch,
  toRenderableIncomingDoc,
} from "../helpers/renderableIncomingDoc";
import { SentryService } from "../services/SentryService/SentryService";

export const SmartdocWidget: React.FC = () => {
  const {
    security,
    users,
    teams,
    initialDoc,
    initialYjsData,
    editable,
    accountSlug,
    applicationId,
    recordId,
    reportId,
    fieldSlug,
    memberName,
    accessToken,
    env,
    webSocketURL,
    filestackAPIKey,
    platform,
    preset,
    pageWidth,
    tip,
    placeholder,
    aiConfig,
    reportLabel,
    showDocumentTitle,
  } = useAppConfig();
  const { setReportLabel } = useAppConfigActions();
  const effectivePreset =
    preset === "full" && !editable && !!reportId && !fieldSlug ? "documentView" : preset;
  const isDocumentView = effectivePreset === "documentView";
  const collabEntityId = isDocumentView ? reportId ?? recordId : recordId;
  const requiresYjsConnection = editable || isDocumentView;
  const hasTarget = !!collabEntityId && (isDocumentView || !!fieldSlug);
  const hasYjsContext =
    !!accountSlug &&
    !!applicationId &&
    !!memberName &&
    !!accessToken &&
    !!env &&
    !!webSocketURL &&
    hasTarget;
  const enableCollab = requiresYjsConnection && hasYjsContext;
  const yjsConnect = enableCollab && !!collabEntityId;

  const viewRef = useRef<EditorView>();
  // NOTE: The schema is a pure function of the extensions, so it is derived from them
  // rather than read off the live `EditorView`. The view only exists once `Editor` has
  // mounted successfully, and it never does when the initial doc fails to parse - which
  // used to leave `getSchema()` returning `undefined` and crash `populateValue` below.
  // The accessor stays lazy because `extensions` is built further down, after the
  // `yjsProvider` it depends on exists.
  const schemaRef = useRef<EditorSchema>();
  const getSchema = useCallback((): EditorSchema | undefined => schemaRef.current, []);
  const { handleViewportResize } = useIOSViewport({ platform });

  const { yjsProvider, yDoc, connectionStatus } = useYjsProvider({
    enabled: enableCollab,
    connect: yjsConnect,
    applicationId: applicationId ?? "",
    entityId: collabEntityId ?? "",
    fieldSlug,
    mode: isDocumentView ? "document" : "record",
    initialDoc,
    webSocketURL: webSocketURL ?? "",
    accountSlug: accountSlug ?? "",
    memberName: memberName ?? "",
    accessToken: accessToken ?? "",
    env: env ?? "",
    initialYjsData,
    getSchema,
  });

  const hasLocalDocumentState = !!initialYjsData || (!!initialDoc && !isDocEmpty(initialDoc));
  const isDocumentViewLoading =
    isDocumentView && enableCollab && !hasLocalDocumentState && connectionStatus === "connecting";
  const editorKey = `${effectivePreset}-${applicationId ?? "no-app"}-${
    collabEntityId ?? "no-entity"
  }-${editable ? "editable" : "readonly"}`;

  const filestackClient = useMemo(() => {
    return init(filestackAPIKey ?? "");
  }, [filestackAPIKey]);

  const enableAskAi =
    isDocumentView && editable && aiConfig.enabled && !!accountSlug && !!accessToken && !!env;

  const aiService = useMemo(() => {
    if (!enableAskAi) {
      return null;
    }

    return new SSAiExtensionService({
      env,
      accessToken,
      accountSlug,
      usageParams: {
        currentCount: aiConfig.currentCount,
        callsLimit: aiConfig.callsLimit,
        isAdmin: aiConfig.isAdmin,
        setupUrl: `${getEnvBaseUrl(
          env
        )}/${accountSlug}/account-administrator/account-settings?section=ai`,
        userHasOwnKey: aiConfig.userHasOwnKey,
      },
    });
  }, [accessToken, accountSlug, aiConfig, enableAskAi, env]);

  const extensions = useMemo(() => {
    if (effectivePreset === "comments") {
      return commentsPreset({
        filestackClient,
        security,
        users,
        teams,
        yjsProvider,
        tip,
        placeholder,
        platform,
      });
    }

    if (effectivePreset === "simple") {
      return simplePreset({
        yjsProvider,
        tip,
        placeholder,
      });
    }

    if (effectivePreset === "documentView") {
      return documentViewPreset({
        editable,
        filestackClient,
        security,
        users,
        teams,
        yjsProvider,
        env,
        accessToken,
        accountSlug,
        tip,
        placeholder,
        platform,
        aiService,
      });
    }

    return fullPreset({
      editable,
      filestackClient,
      security,
      users,
      teams,
      yjsProvider,
      env,
      accessToken,
      accountSlug,
      tip,
      placeholder,
      platform,
    });
  }, [
    accessToken,
    accountSlug,
    aiService,
    editable,
    env,
    filestackClient,
    placeholder,
    platform,
    effectivePreset,
    security,
    teams,
    tip,
    users,
    yjsProvider,
  ]);

  const schema = useMemo(() => generateSchema(extensions), [extensions]);

  // Keep `getSchema` (declared above, before `extensions` exists) pointing at the schema
  // of the extension set currently rendered. Written during render so it is already in
  // place when the effects of this commit - including the yjs one - run.
  schemaRef.current = schema;

  // The document is injected by the native app and never validated on the way in, so it
  // can carry node and mark types this schema has no extension for. Reconcile it here -
  // the editor parses `initialDoc` inside its mount effect, where a throw means a blank
  // screen behind the error boundary instead of a partially rendered document.
  const renderableDoc = useMemo(
    () => toRenderableIncomingDoc(initialDoc, schema),
    [initialDoc, schema]
  );

  // The schema is rebuilt whenever the extensions are, so report per incoming document
  // rather than per sanitize pass - otherwise a reconnect would re-report the same doc.
  const reportedDocRef = useRef<Doc | undefined>();

  useEffect(() => {
    if (isSanitizeDocReportEmpty(renderableDoc.report) || reportedDocRef.current === initialDoc) {
      return;
    }

    reportedDocRef.current = initialDoc;

    SentryService.captureException(describeIncomingDocMismatch(renderableDoc));
  }, [initialDoc, renderableDoc]);

  const handleViewCreated = useCallback(
    (view: EditorView): void => {
      viewRef.current = view;

      if (platform === "ios") {
        handleViewportResize();
      }
    },
    [handleViewportResize, platform]
  );

  const handleContentChange = useCallback(
    (doc: Doc): void => {
      const yjsData = !isDocEmpty(yDocToProsemirrorJSON(yDoc) as Partial<Doc>)
        ? fromByteArray(YJS.encodeStateAsUpdate(yDoc))
        : null;

      postMessage({ type: "change", doc, yjsData });
    },
    [yDoc]
  );

  const handleReportLabelChange = useCallback(
    (changedReportLabel: string): void => {
      if (changedReportLabel === reportLabel) {
        return;
      }

      setReportLabel(changedReportLabel);
    },
    [reportLabel, setReportLabel]
  );

  const handleReportLabelBlur = useCallback((changedReportLabel: string): void => {
    postMessage({ type: "changeReportLabel", changedReportLabel });
  }, []);

  const editor = !isDocumentViewLoading ? (
    <Editor
      key={editorKey}
      onViewCreated={handleViewCreated}
      editable={editable}
      initialDoc={renderableDoc.doc}
      extensions={extensions}
      renderBeforeDoc={
        showDocumentTitle ? (
          <ReportLabelInput
            value={reportLabel}
            editable={editable}
            onChange={handleReportLabelChange}
            onBlur={handleReportLabelBlur}
          />
        ) : undefined
      }
      onContentChange={handleContentChange}
      autoFocus={false}
      className={isDocumentView ? "document-view__editor" : undefined}
    />
  ) : null;

  return (
    <div
      className={classnames("rn-editor", platform, {
        "rn-editor--document-view": isDocumentView,
      })}
    >
      {isDocumentView ? (
        <div
          className={classnames("document-view", {
            "document-view--wide": pageWidth === "wide",
          })}
        >
          {isDocumentViewLoading ? (
            <Preloader type="dots" className="document-view__preloader" />
          ) : (
            editor
          )}
        </div>
      ) : (
        editor
      )}
    </div>
  );
};
